import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QPointDateFormat, QPointDateFormatPipe, QPointLocalizationService } from '@qpoint/i18n';
import { QPointInlineEditAdditionDirective } from '../inline-editing-input/inline-edit-addition.directive';
import { CommonModule } from '@angular/common';
import { QPointDateTimePickerComponent } from '../datetime-picker/datetime-picker.component';
import { QPointIconModule } from '@qpoint/icons';
import { QPointValidationMessageComponent } from '../validation-message/validation-message.component';
import { QPointButtonComponent } from '@qpoint/buttons';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'qpoint-inline-edit-date',
    templateUrl: './inline-editing-date.component.html',
    styleUrls: ['./inline-editing-date.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        QPointDateFormatPipe,
        QPointDateTimePickerComponent,
        QPointIconModule,
        FormsModule,
        QPointValidationMessageComponent,
        QPointButtonComponent,
        TranslateModule
    ]
})
export class QPointInlineEditingDateComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  public controlForValidation = new UntypedFormControl();
  public controlForValidationDisplayActive = false;

  constructor(private localizationService: QPointLocalizationService, @Self() @Optional() public control: NgControl, private cdr: ChangeDetectorRef) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }


  actualClass = '';

  public activeInlineEditingInternal$ = new BehaviorSubject(false);

  QPointDateFormat = QPointDateFormat;
  @Input()
  public infoMessageOnEdit: string;

  @Input()
  public activeInlineEditing: boolean | Observable<boolean> = false;

  @Input()
  public loadingIndicator: boolean;
  public readonly$ = new BehaviorSubject(false);

  @ContentChild(QPointInlineEditAdditionDirective, { read: TemplateRef })
  public content: any;
  /***
   * OnChange and OnTouched PlaceHolders for ngValueAccessor Callback registration
   */
  public onChange;
  public onTouched;

  public isDisabled: boolean;

  public inputFieldValue: Date;
  private inputFieldValueChangedSubject: Subject<string> = new Subject<string>();

  private currentValue: Date;

  private ngUnsubscribe = new Subject<void>();

  ngOnInit() {
    this.activeInlineEditingInternal$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {this.readonly$.next(value)});

  }

  ngAfterViewInit(): void {
    timer(1).subscribe(value => {
      this.readonly$.subscribe(read => {
        this.actualClass = read ? 'inlineEditDisabled' : 'justify-content-end';
      });
    });

    if (this.activeInlineEditing instanceof Observable) {
      this.activeInlineEditing
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(value => {
            this.activeInlineEditingInternal$.next(value);
          }
        );
    } else {
      this.activeInlineEditingInternal$.next(this.activeInlineEditing);
    }
    this.cdr.detectChanges();
  }

  // Should be called whenever something in the view has changed

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Is used whenever the control receives a touch event

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.currentValue = obj;
    this.inputFieldValue = this.currentValue;
  }

  inputFieldValueChange($event: any) {
    timer(1).subscribe(() => {
      this.updateControlForValidationMessage();
    });
    this.inputFieldValueChangedSubject.next($event);
    if (!this.activeInlineEditingInternal$.getValue()) {
      this.onChange($event);
    }
  }

  toggleReadOnly() {
    this.readonly$.next(!this.readonly$.getValue());
    if (!this.readonly$.getValue() && !this.inputFieldValue) {
      this.inputFieldValue = this.localizationService.moment();
    } else {
      this.inputFieldValue = this.currentValue;
    }
  }

  writeIdentifierInForm() {
    if (this.controlForValidation.invalid || this.controlForValidation.pending) {
      // do not write invalid fields into parent form
      this.controlForValidationDisplayActive = true;
      return;
    }
    this.readonly$.next(true);
    this.onChange(this.inputFieldValue);
    this.currentValue = this.inputFieldValue;
  }

  setToReadonly() {
    this.inputFieldValue = this.currentValue;
    this.readonly$.next(true);
    this.updateControlForValidationMessage();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private updateControlForValidationMessage() {
    if (this.control && this.control?.control?.validator) {
      this.controlForValidation.patchValue(this.inputFieldValue);
      this.controlForValidation.setErrors(this.control?.control?.validator(this.controlForValidation));
    }
  }
}
